import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { catchError, map, Subject } from 'rxjs';
import { getFormattedAddressPostCodeAndStreet } from '@mwe/utils';
import { Address, IWizardStepComponent } from '@mwe/models';
import { UiComponentsModule } from '@mwe/ui/components';
import { InfoCardType } from '@shared-ikp/model/info-card-type.enum';
import { Company, transformToCompanies } from '@shared-ikp/model/company.model';
import { PaginationComponent } from '@shared-ikp/components/pagination/pagination.component';
import { NoContentComponent } from '@shared-ikp/components/no-content/no-content.component';
import { InfoInputReadOnlyComponent } from '@shared-ikp/components/inputs/info-input-read-only/info-input-read-only.component';
import { InfoCardComponent } from '@shared-ikp/components/cards/info-card/info-card.component';
import { CustomerRegistrationService } from '../../../service/customer-registration/customer-registration.service';
import { UnternehmenService } from '../../../service/company/unternehmen.service';
import { PAGE_SIZE } from '../../../app.constants';
import { SearchBarComponent } from '@shared-ikp/components/inputs/search-bar/search-bar.component';
import { HttpErrorResponse } from '@angular/common/http';
import { createDynamicErrorPopup } from '@shared-ikp/utils/popup';
import { IkpPopupService } from '@shared-ikp/service/popup/popup.service';
import { createTermAndInvitationStatusBasedUnternehmenSearchRequest } from '../../../model/unternehmen-search-request.model';

@Component({
  selector: 'ikp-berater-company-select',
  templateUrl: './company_select.component.html',
  standalone: true,
  imports: [UiComponentsModule, InfoCardComponent, InfoInputReadOnlyComponent, PaginationComponent, NoContentComponent, SearchBarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanySelectComponent implements IWizardStepComponent, OnInit {
  ALL_UNTERNEHMEN_IN_ONE_PAGE = 10000;
  unternehmenService = inject(UnternehmenService);
  registrationService = inject(CustomerRegistrationService);
  popupService = inject(IkpPopupService);
  paginationUpdate$ = new Subject<void>();
  protected readonly InfoCardType = InfoCardType;
  searchTerm = signal<string>('');
  page = signal<number>(1);
  totalCount = signal<number | null>(null);
  totalPages = signal<number>(1);
  unternehmen = signal<Company[]>([]);
  selectedUnternehmen = computed(() => this.registrationService.registrationModel().companies);
  companiesToShow = computed(() => {
    const invitableCompanies = this.unternehmen();
    const selected = this.selectedUnternehmen();
    const unselectedInvitableCompanies = invitableCompanies.filter(company => !selected.some(c => c.gpNr === company.gpNr));

    if (this.page() === 1) {
      return [...selected, ...unselectedInvitableCompanies];
    }
    return unselectedInvitableCompanies;
  });

  ngOnInit() {
    this.loadUnternehmenOpenForInvitation(this.searchTerm(), this.page(), PAGE_SIZE);
    window.scrollTo({ behavior: 'smooth', top: 0, left: 0 });
  }

  loadUnternehmenOpenForInvitation(searchTerm: string, page: number, size: number): void {
    const unternehmenSearchRequest = createTermAndInvitationStatusBasedUnternehmenSearchRequest(searchTerm, page, size, true);
    this.unternehmenService
      .searchUnternehmen(unternehmenSearchRequest)
      .pipe(
        map((response, index) => {
          this.page.set(response.page.number + 1);
          this.totalCount.set(response.page.totalElements);
          this.totalPages.set(response.page.totalPages);
          this.unternehmen.set(transformToCompanies(response.content));
          if (index === 0) {
            if (this.registrationService.preSelectedGpNr() !== null) {
              this.setCheckboxOfCompany(this.registrationService.preSelectedGpNr());
            }
            this.updateNextButtonStatus();
            this.registrationService.preSelectedGpNr.set(null);
          }
        }),
      )
      .subscribe();
  }

  search($event: string) {
    this.searchTerm.set($event);
    this.loadUnternehmenOpenForInvitation(this.searchTerm(), 1, PAGE_SIZE);
  }

  onPageChange(page: number) {
    this.paginationUpdate$.next();
    this.loadUnternehmenOpenForInvitation(this.searchTerm(), page, PAGE_SIZE);
  }

  getCompanyById(gpNr: string): Company {
    return this.unternehmen().find(company => company.gpNr === gpNr);
  }

  isItemChecked(gpNr: string): boolean {
    return this.selectedUnternehmen().some(c => c.gpNr === gpNr);
  }

  checkboxClicked(companyId: string): void {
    const company = this.getCompanyById(companyId) ?? this.selectedUnternehmen().find(c => c.gpNr === companyId);
    if (!company) {
      return;
    }
    this.registrationService.toggleCompany(company);
    this.updateNextButtonStatus();
  }

  setCheckboxOfCompany(companyId: string): void {
    const company = this.getCompanyById(companyId);
    if (!company) {
      this.addPreselectedCompany(companyId);
      return;
    }
    this.registrationService.addCompany(company);
  }

  updateNextButtonStatus() {
    this.registrationService.isNextButtonDisabled.set(!this.validate());
  }

  addPreselectedCompany(companyId: string): void {
    const unternehmenSearchRequest = createTermAndInvitationStatusBasedUnternehmenSearchRequest(
      companyId,
      1,
      this.ALL_UNTERNEHMEN_IN_ONE_PAGE,
      true,
    );
    this.unternehmenService
      .searchUnternehmen(unternehmenSearchRequest)
      .pipe(
        map(response => {
          const preselectedCompany = transformToCompanies(response.content);
          this.registrationService.addCompany(preselectedCompany[0]);
          this.updateNextButtonStatus();
        }),
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          return [];
        }),
      )
      .subscribe();
  }

  validate(): boolean {
    return this.selectedUnternehmen().length > 0;
  }

  hasAllNeededData(): boolean {
    return true;
  }

  submit(): Promise<void> {
    return Promise.resolve(undefined);
  }

  getFormattedAddress(address: Address): string {
    return getFormattedAddressPostCodeAndStreet(address);
  }

  handleError(error: HttpErrorResponse): void {
    const errorTitleKey = error.status === 403 ? 'global.errors.forbidden.title' : 'global.errors.companySection.title';
    const errorMessageKey = error.status === 403 ? 'global.errors.forbidden.errorMessage' : 'global.errors.companySection.errorMessage';
    const errorPopupModel = createDynamicErrorPopup('download-error', errorTitleKey, errorMessageKey);
    this.popupService.handleGenericPopup(errorPopupModel);
  }
}
